import classnames from 'classnames';
import { Fragment, type FunctionComponent, type ReactElement } from 'react';

import { CURRENCY_ITEM, PRICE_ITEM, PRICE_SPEC } from './Price.constants';
import {
  CurrencyType,
  FormatType,
  VatType,
  type PricePropsType,
  type SmartVatPropsType,
  type VatPropsType,
} from './Price.interfaces';

import styles from './Price.module.scss';

export { CurrencyType, FormatType, VatType };

const Vat = ({ vatText, vatAbbr }: VatPropsType): JSX.Element | null => {
  if (vatText && vatAbbr) {
    return (
      <abbr title={vatText} className={styles.vat}>
        &nbsp;{vatAbbr}
      </abbr>
    );
  }

  if (vatText) {
    return <span className={styles.vat}>&nbsp;{vatText}</span>;
  }

  return null;
};

const SmartVat = ({ vat, translations }: SmartVatPropsType): JSX.Element => (
  <>
    {vat === VatType.INCLUDED && (
      <Vat
        vatText={translations?.includedVAT}
        vatAbbr={translations?.includedVATAbbr}
      />
    )}
    {vat === VatType.EXCLUDED && (
      <Vat
        vatText={translations?.excludedVAT}
        vatAbbr={translations?.excludedVATAbbr}
      />
    )}
  </>
);

const cache = new Map();
export const Price = ({
  className,
  currency = CurrencyType.EUR,
  format = FormatType.DEFAULT,
  maximumFractionDigits = 2,
  minimumFractionDigits = 2,
  obsolete = false,
  translations,
  value,
  vat = VatType.NONE,
  forcedLocale = '',
  ...props
}: PricePropsType): JSX.Element => {
  const RootTag = (obsolete ? 'del' : 'span') as unknown as FunctionComponent<
    React.PropsWithChildren<{ className: string; itemType?: string }>
  >;

  if (translations?.free && !value) {
    return (
      <RootTag {...props} className={classnames(styles.root, className)}>
        {translations.free}
      </RootTag>
    );
  }

  const defaultLocale = currency === CurrencyType.GBP ? 'en-GB' : 'fr-FR';
  const cacheKey = `${forcedLocale || defaultLocale}-${currency}`;

  if (!cache.has(cacheKey)) {
    const formatedCurrencySymbol = new Intl.NumberFormat(
      forcedLocale || defaultLocale,
      {
        style: 'currency',
        currency,
        currencyDisplay: 'symbol',
      },
    ).format(0.1);

    const decimalSeparator = formatedCurrencySymbol.replace(/[^,.]/g, '');
    const currencySymbol = formatedCurrencySymbol.replace(/[,.01A-Za-z]/g, '');
    const currencyStrippedSymbol = currencySymbol.replace(/\s/g, '');
    const shouldDisplayCurrencyFirst =
      formatedCurrencySymbol.startsWith(currencySymbol);

    cache.set(cacheKey, {
      decimalSeparator,
      currencySymbol,
      currencyStrippedSymbol,
      shouldDisplayCurrencyFirst,
    });
  }

  const {
    decimalSeparator,
    currencySymbol,
    currencyStrippedSymbol,
    shouldDisplayCurrencyFirst,
  } = cache.get(cacheKey);

  const formatedValue = new Intl.NumberFormat(forcedLocale || defaultLocale, {
    style: 'decimal',
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value || 0);

  const VatWrapper =
    vat !== VatType.NONE && format === FormatType.TAX_SUP ? 'sup' : Fragment;

  let price: ReactElement;
  let priceCurrency: ReactElement | undefined;
  if (!shouldDisplayCurrencyFirst && format === FormatType.SYMBOL_SUP) {
    const [integer, decimal] = formatedValue.split(decimalSeparator);
    price = (
      <span
        className={styles.supContainer}
        itemProp={PRICE_ITEM}
        content={`${value}`}
      >
        {integer}
        <sup>
          <span itemProp={CURRENCY_ITEM} content={currency}>
            {currencyStrippedSymbol}
          </span>
          {decimal}
          <SmartVat vat={vat} translations={translations || {}} />
        </sup>
      </span>
    );
  } else if (!shouldDisplayCurrencyFirst && format === FormatType.SYMBOL) {
    const [integer, decimal] = formatedValue.split(decimalSeparator);
    price = (
      <span itemProp={PRICE_ITEM} content={`${value}`}>
        {integer}
        <span itemProp={CURRENCY_ITEM} {...{ content: currency }}>
          {currencyStrippedSymbol}
        </span>
        {decimal}
        <SmartVat vat={vat} translations={translations || {}} />
      </span>
    );
  } else if (
    shouldDisplayCurrencyFirst &&
    currency === CurrencyType.GBP &&
    format === FormatType.SYMBOL_SUP
  ) {
    price = (
      <span
        itemProp={PRICE_ITEM}
        content={`${value}`}
        className={styles.supContainer}
      >
        {formatedValue}
        <sup>
          <SmartVat vat={vat} translations={translations || {}} />
        </sup>
      </span>
    );
    priceCurrency = (
      <span itemProp={CURRENCY_ITEM} content={currency}>
        {currencySymbol}
      </span>
    );
  } else if (shouldDisplayCurrencyFirst && currency === CurrencyType.GBP) {
    price = (
      <>
        <span itemProp={PRICE_ITEM} content={`${value}`}>
          {formatedValue}
        </span>
        <VatWrapper>
          <SmartVat vat={vat} translations={translations || {}} />
        </VatWrapper>
      </>
    );
    priceCurrency = (
      <span itemProp={CURRENCY_ITEM} content={currency}>
        {currencySymbol}
      </span>
    );
  } else {
    price = (
      <span itemProp={PRICE_ITEM} content={`${value}`}>
        {formatedValue}
      </span>
    );
    priceCurrency = (
      <>
        <span itemProp={CURRENCY_ITEM} content={currency}>
          {currencySymbol}
        </span>
        <VatWrapper>
          <SmartVat vat={vat} translations={translations || {}} />
        </VatWrapper>
      </>
    );
  }

  return (
    <RootTag
      {...props}
      className={classnames(styles.root, className)}
      itemType={PRICE_SPEC}
    >
      {shouldDisplayCurrencyFirst && priceCurrency}
      {price}
      {!shouldDisplayCurrencyFirst && priceCurrency}
    </RootTag>
  );
};

Price.displayName = 'Price';
